<template>
  <div class="page-venues bg-[#FFFCF9]">
    <section class="border-b border-slate-200 bg-white">
      <div class="mx-auto max-w-[1900px] px-4 py-5 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <h1 class="text-xl font-bold text-ink sm:text-2xl">Venues</h1>
            <p class="mt-1 flex min-w-0 items-center gap-1.5 text-sm text-muted"><i class="ri-map-pin-2-fill shrink-0 text-brand"></i><span class="truncate">{{ hasSelectedLocation ? (locationLabel || 'Selected location') : 'Choose a location to start' }}</span></p>
          </div>
          <button type="button" class="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-bold text-ink transition hover:border-brand hover:text-brand sm:rounded-full sm:px-4" @click="openFilters"><i class="ri-equalizer-3-line"></i><span>Filters</span><span v-if="activeEventTypeChip" class="h-2 w-2 rounded-full bg-brand"></span></button>
        </div>
        <div v-if="hasSelectedLocation && eventTypeChips.length > 1" class="mt-4 flex gap-2 overflow-x-auto pb-1">
          <button v-for="chip in eventTypeChips" :key="chip.value || 'all'" type="button" class="shrink-0 rounded-full border px-4 py-2 text-xs font-bold transition" :class="activeEventTypeChip === chip.value ? 'border-brand bg-brand text-white shadow-[0_6px_18px_rgba(255,122,0,.2)]' : 'border-slate-200 bg-white text-muted hover:border-orange-200 hover:text-brand'" @click="selectEventTypeChip(chip.value)">{{ chip.label }}</button>
        </div>
      </div>
    </section>

    <section class="mx-auto w-full max-w-[1900px] px-4 py-7 sm:px-6 lg:px-8 lg:py-9">
      <div class="grid gap-7 xl:grid-cols-[minmax(0,1fr)_minmax(500px,44vw)]">
        <div class="order-last min-w-0 xl:order-none">
          <div class="mb-5 flex items-center justify-between gap-4">
            <h2 class="text-lg font-bold text-ink sm:text-xl">{{ venuesResultCountTitle }} <span v-if="venuesResultLocationTitle" class="font-medium text-muted">near {{ venuesResultLocationTitle }}</span></h2>
            <span v-if="filteredVenues.length" class="hidden rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-muted ring-1 ring-slate-200 sm:inline-flex">{{ filteredVenues.length }} on map</span>
          </div>

          <div v-if="!hasSelectedLocation" class="rounded-[30px] border border-dashed border-orange-200 bg-white px-6 py-14 text-center shadow-[0_12px_35px_rgba(11,33,73,.04)]"><span class="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-50 text-brand"><i class="ri-map-pin-search-line text-3xl"></i></span><h3 class="mt-5 text-xl font-bold text-ink">Start with a location</h3><p class="mx-auto mt-2 max-w-md text-sm leading-6 text-muted">Use the location search in the navbar to discover event spaces near your preferred area.</p></div>

          <div v-else-if="loading" class="grid gap-5 md:grid-cols-2">
            <article v-for="index in 4" :key="index" class="overflow-hidden rounded-[26px] border border-slate-100 bg-white p-3"><div class="aspect-[4/3] animate-pulse rounded-[20px] bg-slate-100"></div><div class="space-y-3 px-2 py-4"><div class="h-5 w-2/3 animate-pulse rounded bg-slate-100"></div><div class="h-4 w-full animate-pulse rounded bg-slate-100"></div><div class="h-4 w-4/5 animate-pulse rounded bg-slate-100"></div></div></article>
          </div>

          <div v-else-if="error" class="flex items-start gap-4 rounded-[26px] border border-red-100 bg-red-50 p-5 text-red-700"><span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white"><i class="ri-error-warning-line text-xl"></i></span><div><p class="font-bold">We couldn’t load venues</p><p class="mt-1 text-sm">{{ error }}</p></div></div>

          <div v-else-if="filteredVenues.length" class="grid gap-5 md:grid-cols-2">
            <article v-for="venue in filteredVenues" :key="venue.id" class="group cursor-pointer overflow-hidden rounded-[28px] border border-slate-100 bg-white p-3 shadow-[0_10px_32px_rgba(11,33,73,.06)] transition duration-300 hover:-translate-y-1 hover:border-orange-100 hover:shadow-[0_18px_45px_rgba(11,33,73,.11)]" role="link" tabindex="0" @click="openVenueDetail(venue)" @keydown.enter.prevent="openVenueDetail(venue)" @keydown.space.prevent="openVenueDetail(venue)">
              <div class="relative aspect-[4/3] overflow-hidden rounded-[22px] bg-gradient-to-br from-orange-50 to-orange-100">
                <img v-if="venue.image" :src="venue.image" :alt="venue.title" class="h-full w-full object-cover transition duration-700 group-hover:scale-105">
                <div v-else class="flex h-full items-center justify-center"><span class="flex h-20 w-20 items-center justify-center rounded-3xl bg-white/80 text-3xl font-bold text-brand shadow-sm">{{ venue.title.slice(0, 1) }}</span></div>
                <div class="absolute inset-0 bg-gradient-to-t from-ink/45 via-transparent to-transparent"></div>
                <div class="absolute inset-x-0 top-0 flex items-start justify-between gap-3 p-3"><span class="rounded-full bg-white/95 px-3 py-1.5 text-[11px] font-bold text-ink shadow-sm backdrop-blur">{{ venue.typeLabel }}</span><span v-if="venue.distanceLabel" class="rounded-full bg-ink/80 px-3 py-1.5 text-[11px] font-bold text-white backdrop-blur"><i class="ri-navigation-fill mr-1 text-orange-300"></i>{{ venue.distanceLabel }}</span></div>
              </div>
              <div class="px-2 pb-2 pt-4">
                <div class="flex items-start justify-between gap-3"><h3 class="line-clamp-1 text-lg font-bold text-ink transition group-hover:text-brand">{{ venue.title }}</h3><span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50 text-brand transition group-hover:bg-brand group-hover:text-white"><i class="ri-arrow-right-up-line"></i></span></div>
                <p class="mt-1.5 line-clamp-2 min-h-[44px] text-sm leading-[1.55] text-muted">{{ venue.tagLine || 'Explore this venue profile for event details and enquiry options.' }}</p>
                <div v-if="venue.highlights.length" class="mt-3 flex flex-wrap gap-1.5"><span v-for="item in venue.highlights.slice(0, 3)" :key="item" class="rounded-full bg-orange-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-brand">{{ item }}</span></div>
                <div class="mt-4 border-t border-slate-100 pt-3 text-xs text-muted"><p class="flex items-start gap-2"><i class="ri-map-pin-2-fill mt-0.5 shrink-0 text-brand"></i><span class="min-w-0">{{ displayAddress(venue) }} <button v-if="shouldTruncateAddress(venue.address)" type="button" class="address-toggle" @click.stop="toggleAddress(venue)">{{ isAddressExpanded(venue) ? 'Less' : 'More' }}</button></span></p><p class="mt-2 flex items-center gap-2"><i class="ri-calendar-check-line text-brand"></i>{{ venue.estLabel }}</p></div>
              </div>
            </article>
          </div>

          <div v-else class="rounded-[30px] border border-dashed border-orange-200 bg-white px-6 py-14 text-center"><span class="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-50 text-brand"><i class="ri-building-line text-3xl"></i></span><h3 class="mt-5 text-xl font-bold text-ink">No matching venues yet</h3><p class="mx-auto mt-2 max-w-md text-sm leading-6 text-muted">Try another event type or choose a nearby location to broaden your search.</p><button type="button" class="mt-5 rounded-full border border-orange-200 px-5 py-2.5 text-sm font-bold text-brand transition hover:bg-orange-50" @click="selectEventTypeChip('')">Clear filters</button></div>
        </div>

        <aside ref="mapShell" class="order-first self-start xl:order-none xl:sticky xl:top-4">
          <div class="relative overflow-hidden rounded-[30px] border border-white bg-white p-2 shadow-[0_18px_50px_rgba(11,33,73,.12)] xl:h-[calc(100vh-2rem)]"><div class="pointer-events-none absolute left-6 top-6 z-[500] rounded-full bg-white/95 px-4 py-2 text-xs font-bold text-ink shadow-lg backdrop-blur"><i class="ri-map-2-line mr-1.5 text-brand"></i>Explore the area</div><div class="venue-map-frame h-full overflow-hidden rounded-[24px] bg-slate-100"><div id="venues-map" class="venue-map-surface w-full" :style="mobileMapParallaxStyle"></div></div></div>
        </aside>
      </div>
    </section>

    <transition name="filter-fade">
      <div v-if="showFilterModal" class="fixed inset-0 z-[120] flex justify-end bg-ink/60 backdrop-blur-sm" @click.self="closeFilters">
        <section class="h-full w-full max-w-md overflow-y-auto bg-[#FFFCF9] p-5 shadow-2xl sm:p-7">
          <div class="flex items-start justify-between gap-4 border-b border-orange-100 pb-5">
            <div>
              <p class="text-xs font-bold uppercase tracking-[0.18em] text-brand">Refine your results</p>
              <h2 class="mt-1 text-2xl font-bold text-ink">Venue filters</h2>
              <p class="mt-1 text-sm text-muted">Choose the kind of event you are planning.</p>
            </div>
            <button type="button" class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-ink transition hover:border-brand hover:text-brand" aria-label="Close filters" @click="closeFilters">
              <i class="ri-close-line text-xl"></i>
            </button>
          </div>

          <p class="mt-6 text-xs font-bold uppercase tracking-[0.14em] text-muted">Event type</p><div class="mt-3 flex flex-wrap gap-2">
            <button
              v-for="chip in eventTypeChips"
              :key="chip.value || 'all'"
              type="button"
              class="rounded-full border px-4 py-2.5 text-sm font-bold transition"
              :class="activeEventTypeChip === chip.value ? 'border-brand bg-brand text-white shadow-sm' : 'border-slate-200 bg-white text-muted hover:border-brand hover:text-brand'"
              @click="selectEventTypeChip(chip.value)"
            >
              {{ chip.label }}
            </button>
          </div>
          <button type="button" class="mt-8 w-full rounded-2xl bg-ink px-5 py-3.5 text-sm font-bold text-white transition hover:bg-brand" @click="closeFilters">Show {{ filteredVenues.length }} venue{{ filteredVenues.length === 1 ? '' : 's' }}</button>
        </section>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import userApi from '@/services/user/api'
import vendorApi from '@/services/vendor/api'
import { useSearchStore } from '@/stores/search'

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})

const route = useRoute()
const router = useRouter()
const searchStore = useSearchStore()

const venues = ref([])
const venueEventHostedOptions = ref([])
const loading = ref(true)
const error = ref('')
const map = ref(null)
const mapShell = ref(null)
const mapParallaxOffset = ref(0)
const markers = ref([])
const expandedAddressIds = ref(new Set())
const showFilterModal = ref(false)

const requestId = computed(() => String(route.query.id || '').trim())
const latitude = computed(() => String(route.query.latitude || route.query.lat || searchStore.lat || '').trim())
const longitude = computed(() => String(route.query.longitude || route.query.lng || searchStore.lng || '').trim())
const eventType = computed(() => String(route.query.event_type || '').trim())
const activeEventTypeChip = computed(() => eventType.value)
const locationLabel = computed(() => String(route.query.location || searchStore.locationText || '').trim())
const hasSelectedLocation = computed(() => Boolean(locationLabel.value || (latitude.value && longitude.value)))
const mobileMapParallaxStyle = computed(() => ({
  transform: `translate3d(0, ${mapParallaxOffset.value}px, 0)`
}))
const detailQuery = computed(() => ({
  location: route.query.location || undefined,
  id: route.query.id || undefined,
  event_type: route.query.event_type || undefined,
  latitude: latitude.value || undefined,
  longitude: longitude.value || undefined
}))

const eventTypeChips = computed(() => [
  { label: 'All', value: '' },
  ...venueEventHostedOptions.value.map((item) => ({
    label: item,
    value: item
  }))
])

const normalizeArrayPayload = (data, keys = []) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key]
  }
  return []
}

const normalizeOptionValue = (item) => {
  if (typeof item === 'string') return item.trim()
  return String(item?.title || item?.name || item?.label || item?.value || item?.type || '').trim()
}

const parseCoordinate = (...values) => {
  for (const value of values) {
    const normalized = Number(String(value ?? '').trim())
    if (Number.isFinite(normalized)) return normalized
  }
  return null
}

const normalizeVenue = (item = {}, index = 0) => ({
  id: String(item?.venue_id || item?.vendor_business_venue_id || item?.id || `venue-${index}`),
  businessId: String(item?.business_id || item?.businessId || item?.business_vendor_id || item?.vendor_business_id || '').trim(),
  vendorId: String(item?.vendor_id || item?.vendorId || item?.s_id || '').trim(),
  title: String(item?.title || item?.name || 'Untitled venue').trim(),
  image: String(item?.cover_image || item?.image || '').trim(),
  tagLine: String(item?.tag_line || item?.tagLine || '').trim(),
  about: String(item?.about || '').trim(),
  address: String(item?.address || 'Location unavailable').trim(),
  latitude: parseCoordinate(item?.latitude, item?.lat, item?.venue_latitude, item?.location_latitude, item?.location_lat),
  longitude: parseCoordinate(item?.longitude, item?.lng, item?.lon, item?.venue_longitude, item?.location_longitude, item?.location_lng, item?.location_lon),
  highlights: String(item?.highlights || '').split(',').map((entry) => entry.trim()).filter(Boolean),
  typeLabel: String(item?.type_name || item?.type || 'Venue').trim() || 'Venue',
  estLabel: item?.est_year ? `Est. ${item.est_year}` : 'Year not specified',
  distanceLabel: item?.distance ? `${Number(item.distance).toFixed(1)} km away` : ''
})

const venueDetailQuery = (venue = {}) => ({
  ...detailQuery.value,
  vendor_id: venue.vendorId || undefined,
  business_id: venue.businessId || undefined
})

const filteredVenues = computed(() => venues.value)

const truncateHeadingLocation = (value, maxLength = 30) => {
  const text = String(value || '').trim()
  if (!text) return ''
  return text.length > maxLength ? `${text.slice(0, maxLength).trim()}...` : text
}

const venuesResultCountTitle = computed(() => {
  if (!hasSelectedLocation.value) return 'Explore venues near you'
  const count = filteredVenues.value.length
  return count ? `${count} venue${count === 1 ? '' : 's'}` : 'No venues found nearby'
})

const venuesResultLocationTitle = computed(() => {
  if (!hasSelectedLocation.value || !filteredVenues.value.length) return ''
  return truncateHeadingLocation(locationLabel.value || 'your location')
})

const addressKey = (venue) => String(venue?.id || venue?.title || venue?.address || '')
const shouldTruncateAddress = (value) => String(value || '').trim().length > 40
const isAddressExpanded = (venue) => expandedAddressIds.value.has(addressKey(venue))
const displayAddress = (venue) => {
  const address = String(venue?.address || 'Location unavailable').trim()
  if (!shouldTruncateAddress(address) || isAddressExpanded(venue)) return address
  return `${address.slice(0, 38).trim()}...`
}
const toggleAddress = (venue) => {
  const key = addressKey(venue)
  const next = new Set(expandedAddressIds.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  expandedAddressIds.value = next
}

const openFilters = () => {
  showFilterModal.value = true
}

const closeFilters = () => {
  showFilterModal.value = false
}

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;')

const getVenuePopupHtml = (venue) => `
  <div style="width:260px;">
    <div style="height:120px;border-radius:14px;overflow:hidden;background:#f5efe8;margin-bottom:10px;">
      ${venue.image ? `<img src="${escapeHtml(venue.image)}" alt="${escapeHtml(venue.title)}" style="width:100%;height:100%;object-fit:cover;" />` : `<div style="height:100%;display:flex;align-items:center;justify-content:center;font-weight:700;color:#c69225;font-size:30px;">${escapeHtml(venue.title.slice(0, 1))}</div>`}
    </div>
    <div style="font-weight:700;font-size:15px;line-height:1.25;color:#1f1208;margin-bottom:4px;">${escapeHtml(venue.title)}</div>
    <div style="font-size:12px;color:#7b624d;margin-bottom:8px;">${escapeHtml(venue.address || 'Location unavailable')}</div>
    <button type="button" data-venue-id="${escapeHtml(venue.id)}" style="border:0;background:#ff310f;color:white;border-radius:999px;padding:8px 12px;font-size:12px;font-weight:700;cursor:pointer;">Open venue</button>
  </div>
`

const openVenueDetail = (venue) => {
  if (!venue?.id) return
  router.push({
    name: 'venue-invite-detail',
    params: { venueId: venue.id },
    query: venueDetailQuery(venue)
  })
}

const loadVenueEventHostedOptions = async () => {
  try {
    const { data } = await vendorApi.getVenueEventsHosted(new FormData())
    const options = normalizeArrayPayload(data, ['events', 'venue_events_hosted', 'items', 'list'])
      .map(normalizeOptionValue)
      .filter(Boolean)
    venueEventHostedOptions.value = [...new Set(options)]
  } catch {
    venueEventHostedOptions.value = []
  }
}

const loadVenues = async () => {
  if (!hasSelectedLocation.value) {
    venues.value = []
    loading.value = false
    error.value = ''
    return
  }
  loading.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('id', requestId.value || '')
    formData.append('latitude', latitude.value || '')
    formData.append('longitude', longitude.value || '')
    formData.append('event_type', eventType.value || '')
    const { data } = await userApi.getVenuesInvite(formData)
    const items = Array.isArray(data?.data) ? data.data : Array.isArray(data?.list) ? data.list : []
    venues.value = items.map(normalizeVenue)
  } catch (err) {
    error.value = err?.message || 'Failed to load venues.'
    venues.value = []
  } finally {
    loading.value = false
  }
}

const selectEventTypeChip = async (value) => {
  const nextValue = String(value || '').trim()
  if (nextValue === eventType.value) return

  await router.replace({
    query: {
      ...route.query,
      event_type: nextValue || undefined
    }
  })
}

let mapParallaxFrame = 0

const updateMapParallax = () => {
  if (typeof window === 'undefined' || mapParallaxFrame) return

  mapParallaxFrame = window.requestAnimationFrame(() => {
    mapParallaxFrame = 0

    if (window.innerWidth >= 1280 || !mapShell.value) {
      mapParallaxOffset.value = 0
      return
    }

    const rect = mapShell.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight || 1
    const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height)
    const clamped = Math.min(1, Math.max(0, progress))
    mapParallaxOffset.value = Math.round((clamped - 0.5) * 28)
  })
}

const ensureMap = () => {
  if (map.value) return
  map.value = L.map('venues-map').setView([20.5937, 78.9629], 5)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors'
  }).addTo(map.value)
}

const dropMarkers = () => {
  if (!map.value) return
  markers.value.forEach((marker) => marker.remove())
  markers.value = []

  const markerVenues = filteredVenues.value.filter((item) => Number.isFinite(item.latitude) && Number.isFinite(item.longitude))
  markerVenues.forEach((venue) => {
    const marker = L.marker([venue.latitude, venue.longitude]).addTo(map.value)
    marker.bindPopup(getVenuePopupHtml(venue))
    marker.on('click', () => openVenueDetail(venue))
    marker.on('popupopen', (event) => {
      event.popup.getElement()?.querySelector('[data-venue-id]')?.addEventListener('click', () => openVenueDetail(venue))
    })
    markers.value.push(marker)
  })

  map.value.invalidateSize()

  if (markers.value.length) {
    const group = L.featureGroup(markers.value)
    map.value.fitBounds(group.getBounds().pad(0.2))
  } else {
    map.value.setView([20.5937, 78.9629], 5)
  }
}

onMounted(async () => {
  ensureMap()
  updateMapParallax()
  window.addEventListener('scroll', updateMapParallax, { passive: true })
  window.addEventListener('resize', updateMapParallax)
  requestAnimationFrame(() => {
    map.value?.invalidateSize()
  })
  await loadVenueEventHostedOptions()
  await loadVenues()
  dropMarkers()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateMapParallax)
  window.removeEventListener('resize', updateMapParallax)
  if (mapParallaxFrame) window.cancelAnimationFrame(mapParallaxFrame)
  if (map.value) map.value.remove()
})

watch(
  () => [route.fullPath, searchStore.lat, searchStore.lng],
  async () => {
    await loadVenues()
    dropMarkers()
  }
)

watch(filteredVenues, () => {
  dropMarkers()
})
</script>

<style scoped>
.page-venues {
  min-height: 100vh;
}

.address-toggle {
  margin-left: 4px;
  border: 0;
  background: transparent;
  padding: 0;
  font-size: 12px;
  font-weight: 700;
  color: #ff7a00;
  cursor: pointer;
}

.address-toggle:hover {
  color: #e56700;
}

.filter-fade-enter-active,
.filter-fade-leave-active {
  transition: opacity 0.18s ease;
}

.filter-fade-enter-from,
.filter-fade-leave-to {
  opacity: 0;
}

@media (max-width: 1279px) {
  .venue-map-frame {
    height: 360px;
  }

  #venues-map {
    height: calc(100% + 36px);
    margin-top: -18px;
    will-change: transform;
  }
}

@media (max-width: 767px) {
  .venue-map-frame {
    height: 320px;
  }

  #venues-map {
    height: calc(100% + 36px);
  }
}

@media (min-width: 1280px) {
  #venues-map {
    height: 100%;
    margin-top: 0;
  }
}
</style>
